<template>
  <div class="bg-white p-4 border-b border-gray-200 transition-all">
    <div class="flex gap-5">
      <!-- Large Image Section -->
      <a
        class="relative w-72 h-48 flex-shrink-0 overflow-hidden rounded-lg border border-gray-100
                hidden sm:block
        shadow-sm group" :href="'/' + pageContext.routeParams.path + '/property/' + props.property.id"
      >
        <img
          :src="images[0]?.url"
          :alt="displayName"
          class="w-full h-full object-cover group-hover:scale-105 transition-transform"
          :class="[images[0].type === 'placeholder' ? 'blur-lg saturate-125 group-hover:brightness-80 transition' : '']"
          loading="lazy"
        >

        <Button
          v-if="images[0].type === 'placeholder' && locale !== 'ja'"
          variant="outline"
          class="absolute top-1/2 -translate-y-1/2 left-1/2 transform -translate-x-1/2
          border-0 text-white bg-brand hover:bg-brand/90 z-5 opacity-0 group-hover:opacity-100
          transition"
          as-child="a"
          target="_blank" :href="formUrl"
        >
          {{ t('property.requestPhotos') }}
        </Button>

        <!-- Tags Overlay -->
        <div v-if="allTags.length" class="absolute top-2 left-2 flex gap-1">
          <span v-for="tag in allTags.slice(0, 2)" :key="tag.id" class="bg-brand/90 backdrop-blur-md text-white text-[10px] font-medium px-2 py-1 rounded shadow-sm">
            {{ t(`tags.${tag.id}`) }}
          </span>
        </div>
      </a>

      <!-- Details Section -->
      <div class="flex-1 min-w-0 flex flex-col justify-between py-1">
        <div>
          <div class="flex justify-between items-start">
            <h2 class="text-xl font-bold text-gray-900 leading-tight mb-1">{{ displayName }}</h2>
            <div class="flex gap-2">
              <button class="text-gray-400 hover:text-red-500 transition-colors bg-gray-50 p-2 rounded-full hover:bg-red-50" @click.stop.prevent="toggleFavorite(property.id)">
                <Heart :size="18" :fill="isFavorited ? 'currentColor' : 'none'" :class="isFavorited ? 'text-red-500' : 'text-gray-600'" />
              </button>
              <!-- TODO -->
              <button
                v-if="false" class="text-gray-400 hover:text-brand transition-colors bg-gray-50 p-2
                rounded-full hover:bg-blue-50"
              >
                <Share2 :size="18" />
              </button>
            </div>
          </div>

          <div class="flex justify-between items-end mb-2">
            <div v-if="property.price" class="text-brand font-bold text-xl drop-shadow-md tracking-tight">{{ formatPrice(property.price) }}</div>
            <div v-if="property.rent" class="text-brand font-bold text-xl drop-shadow-md tracking-tight">{{ formatPrice(property.rent) }} / {{ t('property.month') }}</div>
          </div>

          <div class="flex items-center gap-1 text-gray-500 text-sm mb-4">
            <MapPin :size="14" />
            {{ displayAddress }}
          </div>

          <!-- Stats Grid -->
          <div class="grid grid-cols-3 gap-y-3 gap-x-6 text-sm border-t border-dashed border-gray-200 pt-4">
            <div class="flex flex-col">
              <span class="text-xs text-gray-400 uppercase tracking-wider font-medium">{{ t('property.property_type') }}</span>
              <span class="font-semibold text-gray-700">{{ property.building.type ? t('enums.homeType.' + property.building.type) : '' }}</span>
            </div>
            <div class="flex flex-col">
              <span class="text-xs text-gray-400 uppercase tracking-wider font-medium">{{ t('property.size') }}</span>
              <div class="flex items-center gap-1 font-semibold text-gray-700">
                <Maximize :size="14" class="text-gray-400" />
                <span v-if="size">{{ formatArea(size) }}</span>
                <span v-if="size && property.layout?.roomCount">/</span>
                <template v-if="property.layout?.roomCount">{{ property.layout?.roomCount }}{{ t('property.rooms_suffix', 'r') }}</template>
              </div>
            </div>
            <div v-if="yearBuilt" class="flex flex-col">
              <span class="text-xs text-gray-400 uppercase tracking-wider font-medium">{{ t('property.built') }}</span>
              <div class="flex items-center gap-1 font-semibold text-gray-700">
                <Calendar :size="14" class="text-gray-400" /> {{ yearBuilt }}
              </div>
            </div>
          </div>
        </div>

        <!-- Action Bar -->
        <div class="mt-4 flex gap-3">
          <Button
            class="bg-brand hover:bg-brand/90 text-white flex-1 cursor-pointer"
            :href="'/' + pageContext.routeParams.path + '/property/' + props.property.id"
            as-child="a"
          >
            {{ t('property.viewDetails') }}
          </Button>
          <Button
            v-if="locale !== 'ja'" variant="outline" class="border-gray-300 text-gray-700 hover:bg-gray-50"
            as-child="a" target="_blank"
            :href="formUrl"
          >
            {{ t('property.askQuestion') }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { Heart, Share2, MapPin, Maximize, Calendar } from 'lucide-vue-next';
import { filterTags, type Tag } from '@repo/modules/tags';
import { usePageContext } from "vike-vue/usePageContext";
import Button from './Button.vue';
import { usePhotos } from '../composables/photos'
import { useCurrency, useAreaUnits } from '../composables/units'
import { useFavorites } from '../composables/favorites';
import { useInquiry } from '../composables/inquiry'
import type { Deal, Agent } from '@repo/types'

const pageContext = usePageContext();

const props = defineProps<{
  property: Deal;
  agents?: Agent[];
  photoPriority?: 'reins' | 'zumen'
}>();

const { t, locale } = useI18n();

const displayName = computed(() => {
  return props.property.i18n?.[locale.value]?.name || props.property.name || props.property.building.name;
});

const displayAddress = computed(() => {
  const localizedDeal = props.property.i18n?.[locale.value];
  if (localizedDeal?.building?.addressComponents) {
    const { ken, shi, choaza } = localizedDeal.building.addressComponents;
    return [choaza, shi, ken].filter(Boolean).join(', ');
  }
  return props.property.building.address;
});

const allTags = computed<Tag[]>(() => {
    if (!props.property.labels) return [];
    return filterTags(props.property.labels).sort((a) => a.primary ? -1 : 1);
});

const size = computed(() => props.property.land?.sqm || props.property.sqm);

const yearBuilt = computed(() => {
    if (props.property.building.builtAt) {
        return new Date(props.property.building.builtAt).getFullYear();
    }
    return null;
});

function capitalize(s: string): string {
  if (typeof s !== 'string' || s.length === 0) {
    return '';
  }
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const deal = computed(() => props.property)

const { images } = usePhotos(deal, { placeholder: true, priority: props.photoPriority, thumbnail: true })

const { format: formatPrice } = useCurrency(pageContext.data.currencies);
const { format: formatArea } = useAreaUnits();

const { isFavorite, toggleFavorite } = useFavorites();
const isFavorited = computed(() => isFavorite(deal.value.id));

const { formUrl } = useInquiry(deal, { agents: props.agents })
</script>
